{
    This file is part of the Fresnel Library.
    Copyright (c) 2025 by the FPC & Lazarus teams.

    Pas2js Fresnel interface - Webassembly rendering API

    See the file COPYING.modifiedLGPL.txt, included in this distribution,
    for details about the copyright.

    This program is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

 **********************************************************************}
{$mode objfpc}
{$h+}
{$modeswitch externalclass}
{$modeswitch advancedrecords}

{$DEFINE IMAGE_USEOSC}

// This units implements the abstract base browser support for fresnel
// - for everything in the main thread use fresnel.simple.pas2js.wasmapi
// - for everything webworker use fresnel.web.pas2js.wasmapi

unit Fresnel.Browser.Pas2js.WasmAPI;

interface

// Define this to disable API Logging altogether
{$DEFINE NOLOGAPICALLS}

uses
  Classes, SysUtils, Types, System.UITypes,
  Web, JS, wasienv, WebAssembly,
  Fresnel.Keys,
  Fresnel.Wasm.Shared,
  Fresnel.Shared.Pas2js,
  Fresnel.Messages.Pas2js.WasmAPI,
  Fresnel.MenuBuilder.Pas2js.WasmAPI,
  Fresnel.SelfieSegmentation.Pas2js,
  Fresnel.ResizePixelRatio.Observer.Pas2js;

type

  TWasmFresnelBrowserApi = class;

  TTimerCallback = procedure (aCurrentTicks, aPreviousTicks : NativeInt);

  { TWindowReference }

  TWindowReference = class (TObject)
  private
    FIsPopup : Boolean;
    FLeft, FTop : TFresnelFloat;
    FWidth, FHeight : Integer;
    FPixelRatio : TFresnelFloat;

    FWindowTitle : TJSHTMLElement;

    FLastPointerId : NativeInt;
    FHasPointerId : Boolean;

    { Drag state: while the user drags the title bar, the window follows the
      pointer. The start values are the window position and pointer position at
      the moment the drag began. }
    FDragging : Boolean;
    FDragPointerId : NativeInt;
    FDragStartClientX, FDragStartClientY : TFresnelFloat;
    FDragStartLeft, FDragStartTop : TFresnelFloat;

    { The canvas event listeners are kept so RemoveCanvas can detach them again.
      @Method makes a fresh wrapper each time, so RemoveEventListener needs the
      very reference that AddEventListener was given. }
    FhWheel, FhContextMenu, FhPointerDown, FhPointerUp, FhPointerMove : TJSEventHandler;

    { The title-bar drag listeners, kept for the same reason as the canvas ones. }
    FhTitlePointerDown, FhTitlePointerUp, FhTitlePointerMove : TJSEventHandler;

    function MouseToEvent(aEvent: TJSMouseEvent; aMessageID: TWindowMessageID): TWindowEvent;

    function DoWheel(aEvent: TJSEvent): Boolean;

    function DoContextMenu(aEvent: TJSEvent): Boolean;

    function DoPointerDown(aEvent: TJSEvent): Boolean;
    function DoPointerUp(aEvent: TJSEvent): Boolean;
    function DoPointerMove(aEvent: TJSEvent): Boolean;

    function DoTitlePointerDown(aEvent: TJSEvent): Boolean;
    function DoTitlePointerUp(aEvent: TJSEvent): Boolean;
    function DoTitlePointerMove(aEvent: TJSEvent): Boolean;

    // Apply (virtual) size (width, height, pixelRatio) to actual pixel size (canvas)
    procedure ApplyCanvasResize;

  public
    API : TWasmFresnelBrowserApi;
    WindowCanvasID : TWindowCanvasID;
    CanvasContext : TJSCanvasRenderingContext2D;
    Canvas : TJSHTMLCanvasElement;
    CanvasParent : TJSHTMLElement;
    // Stacking band: 0=normal, 1=above-normal, 2=top. Higher is always on top.
    Band : Integer;
    FMenuBuilder : TMainMenuBuilder;

    constructor Create(const aID : TWindowCanvasID; aAPI : TWasmFresnelBrowserApi; aCanvas : TJSHTMLCanvasElement; aParent : TJSHTMLElement);

    procedure PrepareCanvas;
    procedure RemoveCanvas;

    // Let the user move the window by dragging its title bar
    procedure EnableTitleDrag;

    property Left : TFresnelFloat read FLeft;
    property Top : TFresnelFloat read FTop;
    procedure SetPos(aLeft, aTop : TFresnelFloat);

    property Width : Integer read FWidth;
    property Height : Integer read FHeight;
    procedure SetSize(aWidth, aHeight : TFresnelFloat);

    property PixelRatio : TFresnelFloat read FPixelRatio;

    procedure ChangePixelRatio(aNewPixelRatio : TFresnelFloat);

    // Send all further pointer events to this window, using the pointer last seen going down
    procedure CapturePointer;
    // Stop sending all pointer events to this window
    procedure ReleaseCapturedPointer;

    property WindowTitle : TJSHTMLElement read FWindowTitle;
    property MenuBuilder : TMainMenuBuilder read FMenuBuilder write FMenuBuilder;
    property IsPopup : Boolean read FIsPopup write FIsPopup;
  end;

  { TVideoReference }

  TVideoFrameCallback = reference to procedure (aTimeStamp : TJSDOMHighResTimeStamp; aSnapShot : TJSImageBitmap);
  TCaptureStartedCallback = reference to procedure (aSuccess, aWithAudio : Boolean);
  TCaptureErrorCallback = reference to procedure;

  TVideoReference = class
  private
    FID : TVideoElementID;
    FVideoElement : TJSHTMLVideoElement;
    FCanvas : TJSHTMLCanvasElement; // lazy initialization
    FRequestVideoFrameID : Integer;
    FAPI : TWasmFresnelBrowserApi;
    FIsDisplayMedia: Boolean;

  public
    constructor Create(const aID : TVideoElementID; aAPI : TWasmFresnelBrowserApi);

    property ID : TVideoElementID read FID;
    property IsDisplayMedia: Boolean read FIsDisplayMedia write FIsDisplayMedia;

    procedure StartCapture(const aDeviceID : String; aWidth, aHeight : Integer;
                           aNotifyFrameCallback : TVideoFrameCallback;
                           aNotifyErrorCallback : TCaptureErrorCallback = nil);
    procedure StartDisplayCapture(const aDeviceID : String; aWithAudio : Boolean;
                                  aNotifyCaptureStartedCallback : TCaptureStartedCallback;
                                  aNotifyFrameCallback : TVideoFrameCallback);
    procedure StopCapture;
    function  IsCapturing : Boolean;

    // returns a Promise of an ImageBitmap
    function TakeSnapshot : TJSPromise;

    property API : TWasmFresnelBrowserApi read FAPI;
  end;

  TGetMediaStreamReferenceEvent = function (const aDeviceId: String): TJSMediaStream of object;

  { TWasmFresnelBrowserApi }

  TWasmFresnelBrowserApi = class(TWasmFresnelSharedApi)
  private
    FUseKeyMap : Boolean;
    FWindowsParentRoot : TJSHTMLELement;
    FWindowsCanvases : TJSMap;

    FFocusedCanvas : TWindowReference;
    FLastFocused : TWindowReference;

    FKeyMap : TJSObject;

    FMenuSupport : Boolean;

    FVideoReferences : TJSMap;

    FTimerID : NativeInt;
    FTimerInterval : NativeInt;

    FSelfieSegmentation : TFresnelSelfieSegmentation;

    FOnGetMediaStreamReference : TGetMediaStreamReferenceEvent;

    class var vWindowID : LongInt;
    class var vNextMenuID : LongInt;
    class var vNextVideoID : LongInt;

    class var vKeyMap : TJSObject;
    //class var vKeyCodeMap : TJSObject;

  protected
    // If this value is > 0 then when a DoPointerUp happens, a setTimeout
    // of the given number of miliseonds is initiated
    // If after that delay the value is still > 0 then DelayedProcessingAfterPointerUp
    // is fired, this allows performing actions in the browser-side context
    // of the click, while leaving time for a decision in the wasm-side
    // The value is automatically reset to zero when the processing is fired
    FDelayInMSecForProcessingAfterPointerUp : Integer;
    // Action to execute
    FActionAfterPointerUp : Integer;

    procedure RegisterCommandHandlers; override;

    function SelfieSegmentation : TFresnelSelfieSegmentation;

    function GetNewWindowID : TWindowCanvasID;

    function CreateMenuBuilder(aParent: TJSHTMLELement): TMainMenuBuilder;

    function AllocateWindowReference(aSizeX, aSizeY : LongInt; aPopup : Boolean; aPixelRatio : TFresnelFloat): TWindowReference;
    function DeAllocateWindowReference(const aID: TWindowCanvasID) : TCanvasError;

    function GetWindowRef(const aID: TWindowCanvasID): TWindowReference;
    function GetWindowContext2D(const aID : TWindowCanvasID) : TJSCanvasRenderingContext2D;

    procedure SetWindowsParentRoot(const aValue : TJSHTMLElement);

    procedure SetFocusedCanvas(AValue: TWindowReference);

    property FocusedCanvas : TWindowReference read FFocusedCanvas write SetFocusedCanvas;

    procedure DoTimerTick; virtual; abstract;

    procedure DoEnumerateUserMedia(aCallbackWhenReady : TProc);

    // Forward the browser's devicechange event to WASM, which decides whether
    // to re-enumerate.
    procedure HandleMediaDeviceChange(aEvent : TJSEvent);

    procedure UpdateDisplayMediaINIData;

    function AllocateVideoReference : TVideoReference;
    function DeAllocateVideoElement(const aID : TVideoElementID) : TCanvasError;
    function GetVideoReference(const aID : TVideoElementID) : TVideoReference;

    FClipboardText : String;
    FClipboardExpirationTimestamp : NativeInt;
    FClipboardTextPending : Boolean;
    FClipboardReadCallbacks : TJSArray;
    // Token incremented on each clipboard read start. Used by the safety
    // timeout to only fire HandleClipboardReadText('') if it matches the
    // token that was active when the timer was scheduled — prevents a
    // stale timer from clobbering a fresh pending state.
    FClipboardPendingToken : Integer;
    const cClipboardTextTTLinMilliseconds = 1500;
    // Safety timeout: if the readText Promise does not settle within this
    // window (browser paste permission prompt cancelled, OS clipboard
    // hang, etc.) we force-resolve it with empty text to drain the
    // key-event backburner. 3s is long enough for a user to click Allow
    // on Chrome's permission prompt and short enough to avoid a stuck UI.
    const cClipboardSafetyTimeoutMs = 3000;

    // True if synchronous result is available in FClipboardText
    function UpdateClipboardReadText : Boolean;
    procedure DoClipboardReadText(const aCallbackWhenReady : TProc);
    procedure HandleClipboardReadText(const aText : String);
    procedure TimeoutCurrentClipboardRead(aToken : Integer);

    FFullScreenObserver : TResizePixelRatioObserver;
    FFullScreenNotificationPending : Boolean;

    procedure OnFullScreenResizeOrPixelRatioChanged(const aData: TResizePixelRatioEventData);
    procedure HandleScreenResizeOrPixelRatioChange; virtual;

    function GetWindowsParentRootSize: TSizeF;

    procedure DelayedProcessingAfterPointerUp(aAction : integer); virtual;

  public
    constructor Create(aEnv : TPas2JSWASIEnvironment); override;

    property WindowsParentRoot : TJSHTMLElement read FWindowsParentRoot write SetWindowsParentRoot;
    property MenuSupport : Boolean read FMenuSupport write FMenuSupport;
    Property UseKeyMap : Boolean Read FUseKeyMap Write FUseKeyMap;

    procedure InstallGlobalHandlers;

    procedure StartTimerTick;
    procedure StopTimerTick;

    // Window

    function AllocateWindowCanvas(aSizeX, aSizeY : LongInt; aIDPtr: TWasmPointer; aPixelRatioPtr: PFresnelFloat; aPopup : Boolean): TCanvasError;
    function DeAllocateWindowCanvas(const aID: TWindowCanvasID): TCanvasError;

    function SetWindowTitle(const aID: TWindowCanvasID; aTitle: TWasmPointer; aTitleLen: LongInt): TCanvasError;

    function ShowHideWindow(const aID : TWindowCanvasID; aShow: Boolean; const aParentID: TWindowCanvasID): TCanvasError;

    // Change a window's stacking. aAction: 0=set band, 1=bring to front + activate,
    // 2=send to back. aBand (0/1/2) is only used for aAction=0.
    function SetWindowZOrder(const aID: TWindowCanvasID; aBand, aAction: LongInt): TCanvasError;
    // Give the window its band's z-index and move it to the front of its band.
    procedure RaiseWindowInBand(aRef: TWindowReference);
    // Give the window its band's z-index and move it to the back of its band.
    procedure SinkWindowInBand(aRef: TWindowReference);

    // Cursor

    function SetCursor(aTextUTF16: TWasmPointer; aUTF16Size: LongInt) : TCanvasError;

    // Pointer capture

    // Route all further pointer events to the given window
    function SetPointerCapture(const aID: TWindowCanvasID) : TCanvasError;
    // Stop routing all pointer events to the given window
    function ReleasePointerCapture(const aID: TWindowCanvasID) : TCanvasError;

    // Event

    procedure WakeMainThread;

    // Menu

    function AddMenuItem(const aCanvasID : TWindowCanvasID; aParentID : TMenuID; aCaption : TWasmPointer; aCaptionLen : LongInt; aData: TWasmPointer; aFlags : LongInt; aShortCut : LongInt; aMenuID : PMenuID) : TCanvasError;
    function DeleteMenuItem(const aCanvasID : TWindowCanvasID; aMenuID : TMenuID) : TCanvasError;
    function UpdateMenuItem(const aCanvasID : TWindowCanvasID; aMenuID : TMenuID; aFlags : LongInt; aShortCut : LongInt) : TCanvasError;

    // Keys

    function SetSpecialKeyMap(Map : TWasmPointer; aLen : LongInt) : TCanvasError;
    function KeyNameToKeyKind(const aKey: String) : TKeyKind;

    class function GetGlobalKeyNameMap: TJSObject;
    class function KeyNameToKeyKind(aMap : TJSObject;aKey : string) : TKeyKind;
    class function CreateSpecialKeyNameMap : TJSObject;
    class function CreateSpecialKeyCodeMap : TJSObject;

    // Key handlers are global

    procedure SetWindowEventFromKeyboardEvent(aWindowEvent: TWindowEvent; aKeyboardEvent: TJSKeyboardEvent);
    function DoKeyDownEvent(aEvent: TJSEvent): Boolean;
    function DoKeyUpEvent(aEvent: TJSEvent): Boolean;

    // Click & enter/leave handlers to detect loss of focus.

    procedure DoGlobalClick(aEvent: TJSEvent);
    procedure DoGlobalEnter(aEvent: TJSEvent);
    procedure DoGlobalLeave(aEvent: TJSEvent);
    //
    procedure DoBroadcast(aEvent: TJSEvent);
    procedure DoVisibilityChange(aEvent: TJSEvent);

    // Clipboard

    function ClipboardReadText(aUTF16SizePtr, aDataUTF16 : TWasmPointer) : TCanvasError;
    function ClipboardWriteText(aTextUTF16: TWasmPointer; aUTF16Size: LongInt): TCanvasError;

    // UserMedia

    function UserMediaStartCapture(aDeviceID_UTF16 : TWasmPointer; aDeviceID_UTF16Size : Integer;
                                   aVideoID : TWasmPointer;
                                   aResolutionWidth, aResolutionHeight : Integer;
                                   aOptions : Integer
                                   ) : TCanvasError; virtual;

    // DisplayMedia

    function DisplayMediaStartCapture(aVideoIDPtr : TWasmPointer; aWithAudio : Integer
                                     ) : TCanvasError; virtual;

    function DisplayMediaEnumerate(aUTF16StrPtr : TWasmPointer; aUTF16SizePtr : TWasmPointer) : TCanvasError; virtual;

    function GetMediaStreamReference(const aDeviceId: String): TJSMediaStream;
    property OnGetMediaStreamReference : TGetMediaStreamReferenceEvent read FOnGetMediaStreamReference write FOnGetMediaStreamReference;

    // UserMedia & DisplayMedia

    function MediaStopCapture(aVideoID : TVideoElementID) : TCanvasError;
    function MediaIsCapturing(aVideoID : TVideoElementID) : TCanvasError;

  end;

// --------------------------------------------------------------------
// --------------------------------------------------------------------
// --------------------------------------------------------------------
implementation
// --------------------------------------------------------------------
// --------------------------------------------------------------------
// --------------------------------------------------------------------

// Clipboard paste keys get special treatment in DoKeyDown:
// instead of being forwarded right away, the clipboard state is updated,
// and then they are forwarded. This workaround the restriction of the browser
// clipboard being asynchronous while FMX expects synchronous access
// having the FMX thread wait on the asynchronous result is problematic as well
// since clipboard access can be longish...
type
  TClipboardPasteKey = record
    KeyCode, Shift : Integer;
  end;

const
  VK_INSERT = 45;
  cClipboardPasteKeys : array [0..1]  of TClipboardPasteKey = (
    (KeyCode: Ord('V');  Shift: WASM_KEYSTATE_CTRL),
    (KeyCode: VK_INSERT; Shift: WASM_KEYSTATE_SHIFT)
  );

// FlagsToMenuFlags
//
Function FlagsToMenuFlags(Flags : LongInt) : TMenuFlags;

  procedure add(aFlag: LongInt; aMenuFlag : TMenuFlag);
  begin
    if (Flags and aFlag)=aFlag then
      Include(Result,aMenuFlag);
  end;

begin
  Result:=[];
  Add(MENU_FLAGS_INVISIBLE,mfInvisible);
  Add(MENU_FLAGS_CHECKED,mfChecked);
  Add(MENU_FLAGS_RADIO,mfRadio);
end;

type
  { TJSKeyNameObjectCreator }

  TKeyNameObjectCreator = class(TSpecialKeyEnumerator)
    FObj : TJSObject;
    procedure EnumKey(aCode: Integer; aName: string); override;
  end;

procedure TKeyNameObjectCreator.EnumKey(aCode: Integer; aName: string);
begin
  FObj.Properties[aName]:=aCode;
end;

type
  { TKeyCodeObjectCreator }

  TKeyCodeObjectCreator = class(TSpecialKeyEnumerator)
    FObj : TJSObject;
    procedure EnumKey(aCode: Integer; aName: string); override;
  end;

procedure TKeyCodeObjectCreator.EnumKey(aCode: Integer; aName: string);
begin
  FObj.Properties[IntToStr(aCode)]:=aName;
end;

{ TWindowReference }

// Create
//
constructor TWindowReference.Create(const aID: TWindowCanvasID; aAPI: TWasmFresnelBrowserApi; aCanvas: TJSHTMLCanvasElement; aParent: TJSHTMLElement);
begin
  Canvas := aCanvas;
  canvasParent := aParent;
  API := aAPI;
  WindowCanvasID := aID;
  PrepareCanvas;
  if Assigned(aCanvas) then
  begin
    FWidth := aCanvas.width;
    FHeight := aCanvas.height;
  end;
end;

// PrepareCanvas
//
procedure TWindowReference.PrepareCanvas;
begin
  CanvasContext := TJSCanvasRenderingContext2D(Canvas.getcontext('2d'));

  FhContextMenu := @DoContextMenu;
  FhPointerDown := @DoPointerDown;
  FhPointerUp   := @DoPointerUp;
  FhPointerMove := @DoPointerMove;
  FhWheel       := @DoWheel;

  Canvas.AddEventListener('contextmenu', FhContextMenu);
  Canvas.AddEventListener('pointerdown', FhPointerDown);
  Canvas.AddEventListener('pointerup',   FhPointerUp);
  Canvas.AddEventListener('pointermove', FhPointerMove);
  Canvas.AddEventListener('wheel', FhWheel);
end;

// RemoveCanvas
//
procedure TWindowReference.RemoveCanvas;
begin
  { Detach the listeners before the canvas goes away. A form is freed on a later
    task than the pointer-up that closed it, so without this an in-flight pointer
    event could still fire on the freed window reference. }
  if Assigned(Canvas) then
  begin
    Canvas.RemoveEventListener('contextmenu', FhContextMenu);
    Canvas.RemoveEventListener('pointerdown', FhPointerDown);
    Canvas.RemoveEventListener('pointerup',   FhPointerUp);
    Canvas.RemoveEventListener('pointermove', FhPointerMove);
    Canvas.RemoveEventListener('wheel', FhWheel);
  end;
  if Assigned(FWindowTitle) and Assigned(FhTitlePointerDown) then
  begin
    FWindowTitle.RemoveEventListener('pointerdown', FhTitlePointerDown);
    FWindowTitle.RemoveEventListener('pointerup',   FhTitlePointerUp);
    FWindowTitle.RemoveEventListener('pointermove', FhTitlePointerMove);
  end;
  CanvasParent.remove();
  Canvas := nil;
  CanvasParent := nil;
  CanvasContext := nil;
end;

// EnableTitleDrag
//
procedure TWindowReference.EnableTitleDrag;
begin
  if not Assigned(FWindowTitle) then
    Exit;
  { A "move" cursor over the title tells the user the window can be dragged. }
  FWindowTitle.style.setProperty('cursor', 'move');

  FhTitlePointerDown := @DoTitlePointerDown;
  FhTitlePointerUp   := @DoTitlePointerUp;
  FhTitlePointerMove := @DoTitlePointerMove;

  FWindowTitle.AddEventListener('pointerdown', FhTitlePointerDown);
  FWindowTitle.AddEventListener('pointerup',   FhTitlePointerUp);
  FWindowTitle.AddEventListener('pointermove', FhTitlePointerMove);
end;

// DoTitlePointerDown
//
function TWindowReference.DoTitlePointerDown(aEvent: TJSEvent): Boolean;
var
  evt : TJSPointerEvent absolute aEvent;
begin
  if evt.pointerType <> 'mouse' then
    Exit(False);

  evt.preventDefault;
  { Capture the pointer so the drag keeps working even when the pointer leaves
    the title bar (moving fast). }
  evt.currentTargetElement.setPointerCapture(evt.pointerId);

  FDragging := True;
  FDragPointerId := evt.pointerId;
  FDragStartClientX := evt.clientX;
  FDragStartClientY := evt.clientY;
  FDragStartLeft := FLeft;
  FDragStartTop := FTop;

  { Clicking the title also brings the window to the front. }
  if not IsPopup then
    API.FocusedCanvas := Self;

  Result := True;
end;

// DoTitlePointerMove
//
function TWindowReference.DoTitlePointerMove(aEvent: TJSEvent): Boolean;
var
  evt : TJSPointerEvent absolute aEvent;
  lNewLeft, lNewTop : TFresnelFloat;
begin
  if not FDragging then
    Exit(False);

  lNewLeft := FDragStartLeft + (evt.clientX - FDragStartClientX);
  lNewTop  := FDragStartTop  + (evt.clientY - FDragStartClientY);
  { Keep the window inside the desktop on the top and left: it may not move past
    the desktop's own top-left corner. The window is placed relative to the
    desktop, so its left/top may not become negative. }
  if lNewLeft < 0 then
    lNewLeft := 0;
  if lNewTop < 0 then
    lNewTop := 0;

  SetPos(lNewLeft, lNewTop);
  Result := True;
end;

// DoTitlePointerUp
//
function TWindowReference.DoTitlePointerUp(aEvent: TJSEvent): Boolean;
var
  evt : TJSPointerEvent absolute aEvent;
  lEvent : TWindowEvent;
begin
  if not FDragging then
    Exit(False);

  FDragging := False;
  try
    evt.currentTargetElement.releasePointerCapture(FDragPointerId);
  except
    { The browser releases the capture itself on pointer up, so this may find
      nothing to release. }
  end;

  { Tell the wasm side where the window ended up, so the form knows its new
    position (and a later size change does not snap it back to the old spot). }
  lEvent := TWindowEvent.Create(WindowCanvasID, WASMSG_MOVED);
  lEvent.Param0 := Round(FLeft);
  lEvent.Param1 := Round(FTop);
  API.EnqueueEvent(lEvent);

  Result := True;
end;

// SetPos
//
procedure TWindowReference.SetPos(aLeft, aTop : TFresnelFloat);
var
  style  : TJSCSSStyleDeclaration;
begin
  style := CanvasParent.Style;
  style.SetProperty('position', 'absolute');
  style.SetProperty('width', 'fit-content');
  style.SetProperty('left', TJSFresnelFloat(JSValue(aLeft)).toFixed(2) + 'px');
  style.SetProperty('top', TJSFresnelFloat(JSValue(aTop)).toFixed(2) + 'px');
  FLeft := aLeft;
  FTop := aTop;
end;

// SetSize
//
procedure TWindowReference.SetSize(aWidth, aHeight : TFresnelFloat);
var
  newWidth, newHeight : Integer;
begin
  newWidth := Round(aWidth);
  newHeight := Round(aHeight);
  if (newWidth <> FWidth) or (newHeight <> FHeight) then
  begin
    FWidth := Round(aWidth);
    FHeight := Round(aHeight);
    ApplyCanvasResize;
  end;
end;

// ChangePixelRatio
//
procedure TWindowReference.ChangePixelRatio(aNewPixelRatio: TFresnelFloat);
begin
  if FPixelRatio = aNewPixelRatio then
    Exit;

  FPixelRatio := aNewPixelRatio;
  ApplyCanvasResize;
end;

// CapturePointer
//
procedure TWindowReference.CapturePointer;
begin
  if not FHasPointerId then
    Exit;

  try
    Canvas.setPointerCapture(FLastPointerId);
  except
    { The browser refuses the call when the pointer is no longer down.
      That is not an error for us: there is then nothing to capture. }
  end;
end;

// ReleaseCapturedPointer
//
procedure TWindowReference.ReleaseCapturedPointer;
begin
  if not FHasPointerId then
    Exit;

  try
    Canvas.releasePointerCapture(FLastPointerId);
  except
    { The browser releases the capture itself when the pointer goes up, so a
      later release call may find nothing to release. }
  end;
end;

// ApplyCanvasResize
//
procedure TWindowReference.ApplyCanvasResize;
var
  lWindowEvent : TWindowEvent;
begin
  // those are virtual pixels in HTML DOM, the px suffix is there to deceive The Enemy
  Canvas.style.setProperty('width', IntToStr(FWidth) + 'px');
  Canvas.style.setProperty('height', IntToStr(FHeight) + 'px');

  // actual pixels
  Canvas.Width := Round(FWidth * FPixelRatio);
  Canvas.Height := Round(FHeight * FPixelRatio);

  // notify wasm so it can adjust the backing offscreen canvas
  lWindowEvent := TWindowEvent.Create(WindowCanvasID, WASMSG_RESIZE);
  lWindowEvent.Param0 := FWidth;
  lWindowEvent.Param1 := FHeight;
  lWindowEvent.Param2 := Round(FPixelRatio * 1000);
  API.EnqueueEvent(lWindowEvent);
end;

// MouseToEvent
//
function TWindowReference.MouseToEvent(aEvent: TJSMouseEvent; aMessageID: TWindowMessageID): TWindowEvent;
var
  lShiftState : Integer;

  procedure IncludeState(aState : TShiftStateEnum);
  begin
    lShiftState += (1 shl Ord(aState));
  end;

begin
  Result := TWindowEvent.Create(Self.WindowCanvasID, aMessageID);
  Result.param0 := Round(aEvent.OffsetX);
  Result.param1 := Round(aEvent.OffsetY);

  lShiftState := 0;

  if aEvent.buttons <> 0 then
  begin
    // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/buttons
    if (aEvent.buttons and  1) <> 0 then IncludeState(ssLeft);
    if (aEvent.buttons and  2) <> 0 then IncludeState(ssRight);
    if (aEvent.buttons and  4) <> 0 then IncludeState(ssMiddle);
    if (aEvent.buttons and  8) <> 0 then IncludeState(ssExtra1);
    if (aEvent.buttons and 16) <> 0 then IncludeState(ssExtra2);

    if aEvent.detail = 2 then
      IncludeState(ssDouble);
  end;

  if aEvent.altKey   then IncludeState(ssAlt);
  if aEvent.ctrlKey  then IncludeState(ssCtrl);
  if aEvent.shiftKey then IncludeState(ssShift);
  if aEvent.metaKey  then IncludeState(ssMeta);

  Result.param2 := lShiftState;

  // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button
  // (note this doesnt folow the same ordering as buttons)
  case aEvent.button of
    0 : Result.Param3 := Ord(TMouseButton.mbLeft);
    1 : Result.Param3 := Ord(TMouseButton.mbMiddle);
    2 : Result.Param3 := Ord(TMouseButton.mbRight);
    3 : Result.Param3 := Ord(TMouseButton.mbExtra1);
    4 : Result.Param3 := Ord(TMouseButton.mbExtra2);
  else
    Result.Param3 := Ord(TMouseButton.mbLeft);
  end;
end;

// DoWheel
//
function TWindowReference.DoWheel(aEvent: TJSEvent): Boolean;
var
  lWheelEvent : TJSWheelEvent absolute aEvent;
  lWindowEvent : TWindowEvent;
begin
  Result:=True;

  aEvent.PreventDefault;
  aEvent.StopPropagation;

  lWindowEvent := MouseToEvent(lWheelEvent, WASMSG_WHEELY);
  case lWheelEvent.deltaMode of
    0 : lWindowEvent.Param3 := Round(lWheelEvent.deltaY);
    1 : lWindowEvent.Param3 := Round(lWheelEvent.deltaY*12); // arbitrary
    2 : lWindowEvent.Param3 := Round(lWheelEvent.deltaY*600); // arbitrary
  end;
  API.EnqueueEvent(lWindowEvent);
end;

// DoContextMenu
//
function TWindowReference.DoContextMenu(aEvent: TJSEvent): Boolean;
begin
  aEvent.preventDefault();
  Result:=True;
  // TODO : route to FMX context menu
end;

// DoPointerDown
//
function TWindowReference.DoPointerDown(aEvent: TJSEvent): Boolean;
var
  evt : TJSPointerEvent absolute aEvent;
begin
  if evt.pointerType <> 'mouse' then
    Exit(False);

  evt.preventDefault;
  evt.currentTargetElement.setPointerCapture(evt.pointerId);

  { Keep the id: the wasm side asks for capture without knowing it. It is kept
    after the pointer goes up as well, because the wasm side handles the mouse
    up message later and must still be able to release. }
  FLastPointerId := evt.pointerId;
  FHasPointerId := True;

  if not IsPopup then
    API.FocusedCanvas := Self;
  API.EnqueueEvent(MouseToEvent(evt, WASMSG_MOUSEDOWN));

  Result := True;
end;

// DoPointerUp
//
function TWindowReference.DoPointerUp(aEvent: TJSEvent): Boolean;
var
  Evt : TJSPointerEvent absolute aEvent;
  lApi : TWasmFresnelBrowserApi;
begin
  if evt.pointerType <> 'mouse' then Exit(False);

  Result:=True;
  { A pointer up on a dialog button can close the dialog: EnqueueEvent runs the
    event, which frees this window reference before it returns. So read the api
    into a local first and use that afterwards - this.API would already be gone. }
  lApi := API;
  lApi.EnqueueEvent(MouseToEvent(evt,WASMSG_MOUSEUP));

  // capture release is automatic in pointerup
  if lApi.FDelayInMSecForProcessingAfterPointerUp > 0 then
  begin
    window.setTimeout(
      procedure
      begin
        if lApi.FDelayInMSecForProcessingAfterPointerUp > 0 then
        begin
          lApi.FDelayInMSecForProcessingAfterPointerUp := 0;
          lApi.DelayedProcessingAfterPointerUp(lApi.FActionAfterPointerUp);
        end;
      end,
      lApi.FDelayInMSecForProcessingAfterPointerUp
    );
  end;
end;

// DoPointerMove
//
function TWindowReference.DoPointerMove(aEvent: TJSEvent): Boolean;
var
  evt : TJSPointerEvent absolute aEvent;
begin
  if evt.pointerType <> 'mouse' then Exit(False);
  Result := True;
  API.EnqueueEvent(MouseToEvent(evt, WASMSG_MOVE));
end;

// ---------------
// --------------- TVideoReference ---------------
// ---------------

// Create
//
constructor TVideoReference.Create(const aID: TVideoElementID; aAPI: TWasmFresnelBrowserApi);
begin
  FID := aID;
  FAPI := aAPI;
  FRequestVideoFrameID := 0;
  FIsDisplayMedia := False;
  FVideoElement := TJSHTMLVideoElement(document.createElement('video'));
  FVideoElement.setAttribute('autoplay',  '');
  FVideoElement.setAttribute('playsinline', '');
  FVideoElement.style.setProperty('display', 'none');
end;

// StartCapture
//
procedure TVideoReference.StartCapture(
  const aDeviceID: String; aWidth, aHeight: Integer;
  aNotifyFrameCallback : TVideoFrameCallback;
  aNotifyErrorCallback : TCaptureErrorCallback
  );

  procedure DoRequestVideoFrame(aNow : TJSDOMHighResTimeStamp; aMetaData : TJSVideoFrameMetaData);
  begin
    TakeSnapshot._then(
      function (aValue : JSValue) : JSValue
      begin
        aNotifyFrameCallback(aNow, TJSImageBitmap(aValue));
      end
    );
    FVideoElement.requestVideoFrameCallback(@DoRequestVideoFrame);
  end;

var
  lConstraints : TJSObject;

begin
  StopCapture;

  lConstraints := JS.new(['video', JS.new(['width', aWidth, 'height', aHeight, 'deviceId', aDeviceID])]);
  window.navigator.mediaDevices.getUserMedia(lConstraints)._then(
    function (aValue : JSValue) : JSValue
    begin
      FVideoElement.srcObject := TJSHTMLMediaStream(aValue);
      FVideoElement.play;
      if Assigned(aNotifyFrameCallback) then
        FRequestVideoFrameID := FVideoElement.requestVideoFrameCallback(@DoRequestVideoFrame);
    end
  ).catch(
    function (aValue : JSValue) : JSValue
    begin
      StopCapture;
      // The browser denied or failed the camera; tell the WASM side so the
      // stream can report the error instead of staying silently blank.
      if Assigned(aNotifyErrorCallback) then
        aNotifyErrorCallback;
    end
  );
end;

// StartDisplayCapture
//
procedure TVideoReference.StartDisplayCapture(
  const aDeviceID: String; aWithAudio : Boolean;
  aNotifyCaptureStartedCallback : TCaptureStartedCallback;
  aNotifyFrameCallback : TVideoFrameCallback
  );

  procedure DoRequestVideoFrame(aNow : TJSDOMHighResTimeStamp; aMetaData : TJSVideoFrameMetaData);
  begin
    TakeSnapshot._then(
      function (aValue : JSValue) : JSValue
      begin
        aNotifyFrameCallback(aNow, TJSImageBitmap(aValue));
      end
    );
    FVideoElement.requestVideoFrameCallback(@DoRequestVideoFrame);
  end;

var
  lVideoConstraints : TJSMediaConstraints;
begin
  FIsDisplayMedia := True;

  lVideoConstraints := TJSMediaConstraints.new;
  lVideoConstraints.video := True;
  lVideoConstraints.audio := aWithAudio;

  window.navigator.mediaDevices.getDisplayMedia(lVideoConstraints)._then(
    function (aValue : JSValue) : JSValue
    var
      lMediaStream : TJSHTMLMediaStream absolute aValue;
    begin
      if Assigned(aNotifyCaptureStartedCallback) then
        aNotifyCaptureStartedCallback(True, (lMediaStream.getAudioTracks.Length > 0));

      FVideoElement.srcObject := lMediaStream;
      FVideoElement.muted := True;
      FVideoElement.play;
      if Assigned(aNotifyFrameCallback) then
        FRequestVideoFrameID := FVideoElement.requestVideoFrameCallback(@DoRequestVideoFrame);
    end
  ).catch(
    function (aValue : JSValue) : JSValue
    begin
      if Assigned(aNotifyCaptureStartedCallback) then
        aNotifyCaptureStartedCallback(False, False);

      Console.error('DisplayMediaStartCapture error: ', aValue);
    end
  );
end;

// StopCapture
//
procedure TVideoReference.StopCapture;
var
  {%H-}tracks : TJSArray;
begin
  if FVideoElement = nil then
    Exit;

  if FRequestVideoFrameID <> 0 then
  begin
    FVideoElement.cancelVideoFrameCallback(FRequestVideoFrameID);
    FRequestVideoFrameID := 0;
  end;
  if JSValue(FVideoElement.srcObject) then
  begin
    tracks := FVideoElement.srcObject.getTracks;
    asm tracks.forEach(track => track.stop()) end;
    FVideoElement.srcObject := nil;
  end;
end;

// IsCapturing
//
function TVideoReference.IsCapturing: Boolean;
begin
  if JSValue(FVideoElement.srcObject) then
    Result := True
  else result := False;
end;

// TakeSnapshot
//
function TVideoReference.TakeSnapshot: TJSPromise;
begin
  if FCanvas = nil then
    FCanvas := TJSHTMLCanvasElement(document.createElement('canvas'));
  if (FCanvas.width <> FVideoElement.videoWidth) or (FCanvas.height <> FVideoElement.videoHeight) then
  begin
    FCanvas.width := FVideoElement.videoWidth;
    FCanvas.height := FVideoElement.videoHeight;
  end;
  FCanvas.getContextAs2DContext('2d').drawImage(FVideoElement, 0, 0);
  Result := window.createImageBitmap(FCanvas);
end;

// ---------------
// --------------- TWasmFresnelBrowserApi ---------------
// ---------------

// Create
//
constructor TWasmFresnelBrowserApi.Create(aEnv: TPas2JSWASIEnvironment);
begin
  inherited Create(aEnv);

  // Initialize IDs to different ranges to disambiguate when debugging
  if vNextMenuID = 0 then vNextMenuID := 10000;  // Start menu IDs at 10000
  if vNextVideoID = 0 then vNextVideoID := 20000;  // Start video IDs at 20000
  if vWindowID = 0 then vWindowID := 30000;  // Start window IDs at 30000

  WindowsParentRoot := TJSHTMLElement(document.getElementById('desktop'));

  FTimerInterval := 10;

  FWindowsCanvases := TJSMap.new;
  FVideoReferences := TJSMap.new;
  FClipboardReadCallbacks := TJSArray.new;

  UpdateDisplayMediaINIData;

  InstallGlobalHandlers;

  // Tell WASM when the browser's media device set changes (camera plugged in
  // or removed). The listener only forwards the signal; the WASM side owns the
  // decision to re-enumerate. mediaDevices is only present in a secure context.
  if Assigned(Window.navigator.mediaDevices) then
    Window.navigator.mediaDevices.addEventListener('devicechange', @HandleMediaDeviceChange);

  FFullScreenObserver := CreateResizePixelRatioObserver(
    WindowsParentRoot,
    @OnFullScreenResizeOrPixelRatioChanged
  );
end;

// HandleMediaDeviceChange
//
procedure TWasmFresnelBrowserApi.HandleMediaDeviceChange(aEvent : TJSEvent);
var
  lCallback : JSValue;
begin
  if aEvent=nil then ;
  lCallback := InstanceExports['__fresnel_usermedia_devicechanged'];
  if lCallback then
    TUserMediaDeviceChangedCallback(lCallback)();
end;

// RegisterCommandHandlers
//
procedure TWasmFresnelBrowserApi.RegisterCommandHandlers;
begin
  inherited ;
  //
end;

// SelfieSegmentation
//
function TWasmFresnelBrowserApi.SelfieSegmentation : TFresnelSelfieSegmentation;
begin
  if FSelfieSegmentation = nil then
  begin
    FSelfieSegmentation := TFresnelSelfieSegmentation.Create('/SelfieSegmentation');
    FSelfieSegmentation.LoadModel(0);
  end;
  Result := FSelfieSegmentation;
end;

// SetWindowsParentRoot
//
procedure TWasmFresnelBrowserApi.SetWindowsParentRoot(const aValue : TJSHTMLElement);
var
  lPosition : String;
begin
  FWindowsParentRoot := aValue;
  { The windows are placed with position:absolute, so the desktop must be a
    positioning context; otherwise the windows anchor to the page instead of the
    desktop area. Leave a desktop that is already positioned as it is, so a host
    that set its own choice keeps it. A desktop that is not yet attached reports
    an empty position; treat that as needing a positioning context too. }
  if Assigned(FWindowsParentRoot) then
  begin
    lPosition := window.getComputedStyle(FWindowsParentRoot).getPropertyValue('position');
    if (lPosition <> 'relative') and (lPosition <> 'absolute')
       and (lPosition <> 'fixed') and (lPosition <> 'sticky') then
      FWindowsParentRoot.style.setProperty('position', 'relative');
  end;
end;

// GetNewWindowID
//
function TWasmFresnelBrowserApi.GetNewWindowID : TWindowCanvasID;
begin
  Inc(vWindowID);
  Result := vWindowID;
end;

// CreateMenuBuilder
//
function TWasmFresnelBrowserApi.CreateMenuBuilder(aParent : TJSHTMLELement): TMainMenuBuilder;
begin
  Result := TDefaultMainMenuBuilder.Create(Self, aParent);
end;

// AllocateWindowReference
//
function TWasmFresnelBrowserApi.AllocateWindowReference(
  aSizeX, aSizeY: LongInt;
  aPopup: Boolean;
  aPixelRatio : TFresnelFloat
  ) : TWindowReference;
var
  CMenu, CTitle, CParent : TJSHTMLElement;
  lCanvasElement : TJSHTMLCanvasElement;

  lWindowID : TWindowCanvasID;
  idSuffix : String;
begin
  lWindowID := GetNewWindowID;
  idSuffix := lWindowID.ToIDString;

  CParent:=TJSHTMLElement(document.createElement('div'));
  CParent.id := 'ffp' + idSuffix;
  if aPopup then
    CParent.className:='fresnel-popup'
  else CParent.className:='fresnel-window';
  CParent.style.setProperty('display', 'none');
  CParent.style.setProperty('user-select', 'none');
  { Every window starts in the normal band (z-index 0). Stay-on-top raises it. }
  CParent.style.setProperty('z-index', '0');

  WindowsParentRoot.AppendChild(CParent);

  if not aPopup then
  begin
    CTitle := TJSHTMLElement(document.createElement('div'));
    CTitle.id := 'fft' + idSuffix;
    CTitle.className := 'fresnel-window-title';
    CParent.AppendChild(CTitle);
    if MenuSupport then
    begin
      CMenu:=TJSHTMLElement(document.createElement('div'));
      CMenu.id := 'ffm' + idSuffix;
      CMenu.className:='fresnel-window-menu';
      CMenu.style.setProperty('display','block');
      CParent.AppendChild(CMenu);
    end;
  end
  else
  begin
    CTitle := TJSHTMLElement(WindowsParentRoot.QuerySelector('.fresnel-window-title'));
  end;

  lCanvasElement := TJSHTMLCanvasElement(document.createElement('CANVAS'));
  lCanvasElement.id := 'ffc' + idSuffix;
  lCanvasElement.className:='fresnel-window-client';
  lCanvasElement.width := Round(aSizeX * aPixelRatio);
  lCanvasElement.height := Round(aSizeY * aPixelRatio);
  lCanvasElement.style.setProperty('display', 'block');
  lCanvasElement.style.setProperty('width', IntToStr(aSizeX) + 'px');
  lCanvasElement.style.setProperty('height', IntToStr(aSizeY) + 'px');
  CParent.AppendChild(lCanvasElement);

  Result := TWindowReference.Create(lWindowID, Self, lCanvasElement, CParent);
  Result.FWindowTitle := CTitle;
  Result.IsPopup := aPopup;
  { Only a real window has its own title bar to drag; a popup borrows one. }
  if not aPopup then
    Result.EnableTitleDrag;
  Result.FPixelRatio := aPixelRatio;
  { The canvas above is already sized to aSizeX x aSizeY. Record that size on the
    window so a later SetSize with the same size does not resize the canvas again
    and send a spurious resize message. }
  Result.FWidth := aSizeX;
  Result.FHeight := aSizeY;

  if MenuSupport then
    Result.MenuBuilder := CreateMenuBuilder(CMenu);
  FWindowsCanvases.&set(lWindowID, Result);
end;

// DeAllocateWindowReference
//
function TWasmFresnelBrowserApi.DeAllocateWindowReference(const aID: TWindowCanvasID) : TCanvasError;
var
  lWinRef : TWindowReference;
begin
  lWinRef := GetWindowRef(aID);
  if lWinRef = nil then
    Exit(ECANVAS_NOCANVAS);
  lWinRef.RemoveCanvas;
  FWindowsCanvases.delete(aID);
  lWinRef.Free;
  Result := ECANVAS_SUCCESS;
end;

// GetWindowRef
//
function TWasmFresnelBrowserApi.GetWindowRef(const aID: TWindowCanvasID): TWindowReference;
var
  jsRef : JSValue;
  canvasRef : TWindowReference absolute jsRef;
begin
  Result := nil;
  jsRef := FWindowsCanvases.get(aID);
  if jsRef then
    Result := canvasRef;
end;

// GetWindowContext2D
//
function TWasmFresnelBrowserApi.GetWindowContext2D(const aID: TWindowCanvasID): TJSCanvasRenderingContext2D;
var
  lWindowRef : TWindowReference;
begin
  lWindowRef := GetWindowRef(aID);
  if lWindowRef <> nil then
    Result := lWindowRef.CanvasContext
  else
  begin
    Console.Warn('Fresnel: Unknown window ', aID);
    Result := nil;
  end;
end;

// SetFocusedCanvas
//
procedure TWasmFresnelBrowserApi.SetFocusedCanvas(AValue: TWindowReference);
var
  otherID : TWindowCanvasID;
  evt : TWindowEvent;
begin
  if FFocusedCanvas = aValue then Exit;

  otherID := Default(TWindowCanvasID);
  if Assigned(FFocusedCanvas) then
  begin
    if Assigned(aValue) then
      otherID := aValue.WindowCanvasID;
    evt := TWindowEvent.Create(FFocusedCanvas.WindowCanvasID, WASMSG_DEACTIVATE);
    evt.Param0 := otherID;
    EnqueueEvent(evt);
    otherID := FFocusedCanvas.WindowCanvasID;
  end;
  FFocusedCanvas := aValue;
  if Assigned(FFocusedCanvas) then
  begin
    { A window that gains focus (e.g. a click) comes to the front of its band,
      matching the native window managers. It never crosses a band boundary. }
    RaiseWindowInBand(FFocusedCanvas);
    evt := TWindowEvent.Create(FFocusedCanvas.WindowCanvasID, WASMSG_ACTIVATE);
    evt.Param0 := otherID;
    EnqueueEvent(evt);
  end;
end;

// StartTimerTick
//
procedure TWasmFresnelBrowserApi.StartTimerTick;
begin
  FTimerID := Window.setInterval(@DoTimerTick, FTimerInterval);
end;

// StopTimerTick
//
procedure TWasmFresnelBrowserApi.StopTimerTick;
begin
  Window.clearInterval(FTimerID);
  FTimerID := 0;
end;

// InstallGlobalHandlers
//
procedure TWasmFresnelBrowserApi.InstallGlobalHandlers;
begin
  Document.Body.AddEventListener('keydown',    @DoKeyDownEvent);
  Document.Body.AddEventListener('keyup',      @DoKeyUpEvent);
  Document.Body.AddEventListener('click',      @DoGlobalClick);
  Document.Body.AddEventListener('mouseleave', @DoGlobalLeave);
  Document.Body.AddEventListener('mouseenter', @DoGlobalEnter);

  Document.AddEventListener('broadcast',       @DoBroadcast);
  Document.AddEventListener('visibilitychange', @DoVisibilityChange);
end;

// AllocateWindowCanvas
//
function TWasmFresnelBrowserApi.AllocateWindowCanvas(
  aSizeX, aSizeY: LongInt;
  aIDPtr: TWasmPointer;
  aPixelRatioPtr: PFresnelFloat;
  aPopup : Boolean
  ): TCanvasError;
var
  lRef : TWindowReference;
  lPixelRatio : TFresnelFloat;
begin
  {$IFNDEF NOLOGAPICALLS}
  if LogAPICalls then
    LogCall('Canvas.AllocateWindowCanvas(%d,%d)',[SizeX,SizeY]);
  {$ENDIF}

  lPixelRatio := window.devicePixelRatio;

  lRef := AllocateWindowReference(aSizeX, aSizeY, aPopup, lPixelRatio);

  MemoryDataView.setUint32(aIDPtr, lRef.WindowCanvasID, env.IsLittleEndian);
  MemoryDataView.setFloat32(aPixelRatioPtr, lPixelRatio, env.IsLittleEndian);

  Result := ECANVAS_SUCCESS;
end;

// DeAllocateWindowCanvas
//
function TWasmFresnelBrowserApi.DeAllocateWindowCanvas(const aID: TWindowCanvasID): TCanvasError;
begin
  {$IFNDEF NOLOGAPICALLS}
  if LogAPICalls then
    LogCall('Canvas.DeAllocateWindowCanvas(%d)',[aID]);
  {$ENDIF}

  Result := DeAllocateWindowReference(aID);
end;

// SetWindowTitle
//
function TWasmFresnelBrowserApi.SetWindowTitle(const aID: TWindowCanvasID; aTitle: TWasmPointer; aTitleLen: LongInt): TCanvasError;
var
  lWindowRef : TWindowReference;
  lTitle : String;
begin
  lTitle := GetUTF16FromMem(aTitle, aTitleLen);
  {$IFNDEF NOLOGAPICALLS}
  if LogAPICalls then
    LogCall('Canvas.SetWindowTitle(%d,''%s'')',[aID,lTitle]);
  {$ENDIF}

  if aID = 0 then
    Document.title := lTitle
  else
  begin
    lWindowRef := GetWindowRef(aID);
    if lWindowRef = nil then
      Exit(ECANVAS_NOCANVAS);
    lWindowRef.WindowTitle.InnerText := lTitle;
  end;
  Result := ECANVAS_SUCCESS;
end;

// ShowHideWindow
//
function TWasmFresnelBrowserApi.ShowHideWindow(
  const aID: TWindowCanvasID; aShow: Boolean; const aParentID: TWindowCanvasID
  ): TCanvasError;
var
  Ref, ParentRef: TWindowReference;
  ParentElement: TJSHTMLElement;
  style  : TJSCSSStyleDeclaration;
begin
  Ref := GetWindowRef(aID);
  if not Assigned(Ref) then
    Exit(ECANVAS_NOCANVAS);

  style := Ref.CanvasParent.Style;
  if aShow then
    style.SetProperty('display', 'block')
  else style.SetProperty('display', 'none');

  ParentRef := GetWindowRef(aParentID);
  if Assigned(ParentRef) and Assigned(ParentRef.CanvasParent) then
    ParentElement := ParentRef.CanvasParent
  else ParentElement := WindowsParentRoot;

  if ParentElement <> nil then
  begin
    if Ref.CanvasParent.ParentNode <> ParentElement then
      ParentElement.appendChild(Ref.CanvasParent);
    if ParentRef <> nil then
      style.SetProperty('transform', 'translateY(' + TJSFresnelFloat(JSValue(ParentRef.Canvas.offsetTop)).toFixed(2) + 'px)')
  end
  else
  begin
    style.SetProperty('transform', 'none');
  end;

  { A window just shown comes to the front of its band (last-shown frontmost). }
  if aShow and (ParentElement = WindowsParentRoot) then
    RaiseWindowInBand(Ref);

  Result := ECANVAS_SUCCESS;
end;

// SetWindowZOrder
//
function TWasmFresnelBrowserApi.SetWindowZOrder(const aID: TWindowCanvasID; aBand, aAction: LongInt): TCanvasError;
var
  Ref : TWindowReference;
begin
  Ref := GetWindowRef(aID);
  if not Assigned(Ref) then
    Exit(ECANVAS_NOCANVAS);
  case aAction of
    0:
      begin
        // Set the band and bring the window to the front of its (possibly new) band.
        Ref.Band := aBand;
        RaiseWindowInBand(Ref);
      end;
    1:
      begin
        // Bring to front of band and activate.
        RaiseWindowInBand(Ref);
        FocusedCanvas := Ref;
      end;
    2:
      // Send to back of band; focus is left unchanged.
      SinkWindowInBand(Ref);
  end;
  Result := ECANVAS_SUCCESS;
end;

// RaiseWindowInBand
//
procedure TWasmFresnelBrowserApi.RaiseWindowInBand(aRef: TWindowReference);
begin
  if aRef = nil then
    Exit;
  { The z-index carries the band, so a normal window can never cover a stay-on-top
    one. Within the same band (same z-index) the later DOM child wins, so moving to
    the end of the desktop raises the window to the front of its band. }
  aRef.CanvasParent.Style.SetProperty('z-index', IntToStr(aRef.Band));
  if aRef.CanvasParent.ParentNode = WindowsParentRoot then
    WindowsParentRoot.AppendChild(aRef.CanvasParent);
end;

// SinkWindowInBand
//
procedure TWasmFresnelBrowserApi.SinkWindowInBand(aRef: TWindowReference);
begin
  if aRef = nil then
    Exit;
  aRef.CanvasParent.Style.SetProperty('z-index', IntToStr(aRef.Band));
  { First DOM child of the band loses to its siblings, so moving to the front of
    the desktop children lowers the window to the back of its band. }
  if (aRef.CanvasParent.ParentNode = WindowsParentRoot) and (WindowsParentRoot.FirstChild <> nil) then
    WindowsParentRoot.InsertBefore(aRef.CanvasParent, WindowsParentRoot.FirstChild);
end;

// OnFullScreenResizeOrPixelRatioChanged
//
procedure TWasmFresnelBrowserApi.OnFullScreenResizeOrPixelRatioChanged(const aData: TResizePixelRatioEventData);
begin
  // changes may come at high-frequency, debounce the actual notification
  if FFullScreenNotificationPending then Exit;

  // Rate limit to screen refresh and ensure a rendering cycle has happened
  // (otherwise clientWidth & clientHeight may be inconsistent)
  FFullScreenNotificationPending := True;
  window.requestAnimationFrame(
    procedure (aTime: TJSDOMHighResTimeStamp)
    begin
      FFullScreenNotificationPending := False;
      FWindowsCanvases.forEach(
        procedure(value: JSValue; key: JSValue)
        begin
          TWindowReference(value).ChangePixelRatio(aData.PixelRatio);
        end
      );
      HandleScreenResizeOrPixelRatioChange;
    end
  );
end;

// HandleScreenResizeOrPixelRatioChange
//
procedure TWasmFresnelBrowserApi.HandleScreenResizeOrPixelRatioChange;
begin
  // nothing here
end;

// GetWindowsParentRootSize
//
function TWasmFresnelBrowserApi.GetWindowsParentRootSize: TSizeF;
var
  lRect : TJSDOMRect;
begin
  lRect := WindowsParentRoot.getBoundingClientRect;
  Result.cx := lRect.right - lRect.left;
  Result.cy := lRect.bottom - lRect.top;
end;

// DelayedProcessingAfterPointerUp
//
procedure TWasmFresnelBrowserApi.DelayedProcessingAfterPointerUp(aAction: integer);
begin
  // nothing here, meant to be overridden
end;

// SetCursor
//
function TWasmFresnelBrowserApi.SetCursor(aTextUTF16: TWasmPointer; aUTF16Size: LongInt): TCanvasError;
var
  lCursor : String;
begin
  lCursor := GetUTF16FromMem(aTextUTF16, aUTF16Size);
  WindowsParentRoot.style.setProperty('cursor', lCursor);

  Result := ECANVAS_SUCCESS;
end;

// SetPointerCapture
//
function TWasmFresnelBrowserApi.SetPointerCapture(const aID: TWindowCanvasID): TCanvasError;
var
  lRef : TWindowReference;
begin
  lRef := GetWindowRef(aID);
  if not Assigned(lRef) then
    Exit(ECANVAS_NOCANVAS);

  lRef.CapturePointer;
  Result := ECANVAS_SUCCESS;
end;

// ReleasePointerCapture
//
function TWasmFresnelBrowserApi.ReleasePointerCapture(const aID: TWindowCanvasID): TCanvasError;
var
  lRef : TWindowReference;
begin
  lRef := GetWindowRef(aID);
  if not Assigned(lRef) then
    Exit(ECANVAS_NOCANVAS);

  lRef.ReleaseCapturedPointer;
  Result := ECANVAS_SUCCESS;
end;

// WakeMainThread
//
procedure TWasmFresnelBrowserApi.WakeMainThread;
var
  callback : JSValue;
begin
  // if we reach here, we're in the appropriate thread, wake up the wasm
  callback := InstanceExports['__fresnel_main_thread_wake'];
  if callback then
    TProcedure(callback)();
end;

// AddMenuItem
//
function TWasmFresnelBrowserApi.AddMenuItem(const aCanvasID: TWindowCanvasID; aParentID: TMenuID; aCaption: TWasmPointer; aCaptionLen: LongInt;
  aData: TWasmPointer; aFlags: LongInt; aShortCut: LongInt; aMenuID: PMenuID): TCanvasError;

var
  S : String;
  Ref : TWindowReference;
  lMenuID : TMenuID;
  el : TJSHTMLElement;
  lFlags : TMenuFlags;

begin
  S := GetUTF16FromMem(aCaption, aCaptionLen);
  {$IFNDEF NOLOGAPICALLS}
  if LogAPICalls then
    LogCall('FresnelAPI.AddMenuItem(%d,"%s",%d,[%x],[%x])',[aCanvasID,S,aParentID,aData,aMenuID]);
  {$ENDIF}
  if not MenuSupport then
    Exit(ECANVAS_NOMENUSUPPORT);
  Ref := GetWindowRef(aCanvasID);
  if not Assigned(Ref) then
    Exit(ECANVAS_NOCANVAS);
  if not Assigned(Ref.MenuBuilder)then
    Exit(ECANVAS_NOMENUSUPPORT);

  Inc(vNextMenuID);
  lMenuID := vNextMenuID;

  LFlags:=FlagsToMenuFlags(aFlags);
  if Ref.MenuBuilder.AddMenuItem(aParentID,lMenuID,S,lFlags,aShortCut,aData)<>Nil then
  begin
    MemoryDataView.setInt32(aMenuID, lMenuID, Env.IsLittleEndian);
    El:=TJSHTMLELement(Document.GetElementByID('ffm' + aCanvasID.ToIDString));
    if assigned(el) then
      el.style.removeProperty('display');
  end;
  Result:=ECANVAS_SUCCESS;
end;

// DeleteMenuItem
//
function TWasmFresnelBrowserApi.DeleteMenuItem(const aCanvasID : TWindowCanvasID; aMenuID: TMenuID): TCanvasError;
var
  Ref : TWindowReference;
begin
  {$IFNDEF NOLOGAPICALLS}
  if LogAPICalls then
    LogCall('FresnelAPI.AddMenuItem(%d,%d)',[aCanvasID,aMenuID]);
  {$ENDIF}
  if not MenuSupport then
    Exit(ECANVAS_NOMENUSUPPORT);
  Ref:=GetWindowRef(aCanvasID);
  if not assigned(Ref) then
    Exit(ECANVAS_NOCANVAS);
  if not Assigned(Ref.MenuBuilder)then
    Exit(ECANVAS_NOMENUSUPPORT);
  Ref.MenuBuilder.RemoveMenuItem(aMenuID);
  Result:=ECANVAS_SUCCESS;
end;

// UpdateMenuItem
//
function TWasmFresnelBrowserApi.UpdateMenuItem(const aCanvasID : TWindowCanvasID; aMenuID: TMenuID; aFlags: LongInt; aShortCut: LongInt): TCanvasError;
begin
  Result:=ECANVAS_UNSPECIFIED;
  console.log('TWasmFresnelApi.UpdateMenuItem not implemented');
end;

// SetSpecialKeyMap
//
function TWasmFresnelBrowserApi.SetSpecialKeyMap(Map: TWasmPointer; aLen: LongInt): TCanvasError;
var
  Ptr : TWasmPointer;
  V : TJSDataView;
  i,old,new : Integer;
  jsname : jsValue;
  name: string absolute jsname;
  inv : TJSObject;
begin
  {$IFNDEF NOLOGAPICALLS}
  if LogAPICalls then
    LogCall('Canvas.SetSpecialKeyMap([%x],%d)',[Map,aLen]);
  {$ENDIF}
  FKeyMap := CreateSpecialKeyNameMap;
  V:=MemoryDataView;
  Ptr:=Map;
  inv:=CreateSpecialKeyCodeMap;
  for I:=1 to aLen do
  begin
    old:=v.getInt32(Ptr,env.IsLittleEndian);
    inc(Ptr,SizeInt32);
    new:=v.getInt32(Ptr,env.IsLittleEndian);
    inc(Ptr,SizeInt32);
    jsname:=inv.Properties[IntToStr(old)];
    if IsString(jsname) then
      FKeyMap.Properties[name]:=New;
  end;
  Result := (ECANVAS_SUCCESS);
end;

// KeyNameToKeyKind
//
function TWasmFresnelBrowserApi.KeyNameToKeyKind(const aKey: String): TKeyKind;
var
  lMap : TJSObject;
begin
  lMap:=FKeyMap;
  if lMap=Nil then
    lmap:=GetGlobalKeyNameMap;
  Result:=KeyNameToKeyKind(lMap,aKey);
//  Writeln('Mapped ',aKey,' to ',TJSJSON.StringIfy(Result));
end;

// GetGlobalKeyNameMap
//
class function TWasmFresnelBrowserApi.GetGlobalKeyNameMap: TJSObject;
begin
  if vKeyMap = nil then
    vKeyMap := CreateSpecialKeyNameMap;
  Result := vKeyMap;
end;

// KeyNameToKeyKind
//
class function TWasmFresnelBrowserApi.KeyNameToKeyKind(aMap : TJSObject; aKey: string): TKeyKind;
var
  num: JSValue;
begin
  num:=aMap.Properties[aKey];
  Result.IsSpecial:=isDefined(num);
  if Result.IsSpecial then
    Result.KeyCode:=Integer(Num)
  else
    Result.KeyCode:=TJSString(JSValue(aKey)).codePointAt(0);
end;

// CreateSpecialKeyNameMap
//
class function TWasmFresnelBrowserApi.CreateSpecialKeyNameMap: TJSObject;
var
  creator : TKeyNameObjectCreator;
begin
  Result := TJSObject.New;
  creator := TKeyNameObjectCreator.Create;
  try
    creator.FObj := Result;
    EnumSpecialKeys(@creator.EnumKey);
  finally
    creator.Free;
  end;
end;

// CreateSpecialKeyCodeMap
//
class function TWasmFresnelBrowserApi.CreateSpecialKeyCodeMap: TJSObject;
begin
  Result:=TJSObject.New;
  With TKeyCodeObjectCreator.Create do
    try
      FObj:=Result;
      EnumSpecialKeys(@EnumKey);
    finally
      Free;
    end;
end;

// SetWindowEventFromKeyboardEvent
//
procedure TWasmFresnelBrowserApi.SetWindowEventFromKeyboardEvent(
  aWindowEvent : TWindowEvent;
  aKeyboardEvent : TJSKeyboardEvent);
var
  isNormalChar : Boolean;
begin
  isNormalChar := (Length(aKeyboardEvent.Key) = 1)
                  and not (aKeyboardEvent.ctrlKey or aKeyboardEvent.altKey or aKeyboardEvent.metaKey);

  if isNormalChar then
    begin
    aWindowEvent.param0 := TJSString(aKeyboardEvent.Key).charCodeAt(0);
    aWindowEvent.param1 := WASMSG_KEYKIND_SPECIAL_SCANCODE;//WASMSG_KEYKIND_CHAR;
    end
  else if not UseKeyMap then
    begin
    aWindowEvent.param0 :=  Longint(TJSObject(aKeyboardEvent)['keyCode']);
    aWindowEvent.param1 := WASMSG_KEYKIND_CHAR; //WASMSG_KEYKIND_SPECIAL_SCANCODE;
    end
  else
    begin
    aWindowEvent.param0 := KeyNameToKeyKind(aKeyboardEvent.Key).KeyCode;
    aWindowEvent.param1 := WASMSG_KEYKIND_SPECIAL_MAP;
    end;

  aWindowEvent.param2 := TFresnelHelper.EncodeKeyboardShiftState(aKeyboardEvent);
  aWindowEvent.param3 := 0;
end;

// DoKeyDownEvent
//
function TWasmFresnelBrowserApi.DoKeyDownEvent(aEvent: TJSEvent): Boolean;
var
  evt : TWindowEvent;
  keyEvent : TJSKeyboardEvent absolute aEvent;
  //keyKind : TKeyKind;
  i : Integer;
begin
  if FocusedCanvas = nil then Exit(False);

  aEvent.preventDefault;
  aEvent.cancelBubble := True;

  evt := TWindowEvent.Create(FocusedCanvas.WindowCanvasID, WASMSG_KEYDOWN);
  //keyKind := KeyNameToKeyKind(keyEvent.Key);
  //evt.param0 := keyKind.KeyCode;
  //evt.Param1 := Ord(keyKind.isSpecial);
  //evt.param2 := TWindowReference.EncodeShiftState(keyEvent);
  SetWindowEventFromKeyboardEvent(evt, keyEvent);

  // if not a normal key
  if evt.param1 = 0 then
  begin
    for i := 0 to High(cClipboardPasteKeys) do begin
      if     (evt.param0 = cClipboardPasteKeys[i].KeyCode)
         and (evt.param2 = cClipboardPasteKeys[i].Shift) then
      begin
        // Paste shortcut: the matching KEYDOWN is dispatched only AFTER the
        // async clipboard text has been retrieved, so FMX sees the "V" arrive
        // together with fresh clipboard content.
        DoClipboardReadText(
          procedure
          begin
            EnqueueEvent(evt);
          end
        );
        Exit(True);
      end;
    end;
  end;

  // Preserve event ordering when a paste is in flight.
  //
  // Before this, only the paste KEYDOWN (e.g. 'V') was deferred while
  // every subsequent key event was enqueued immediately. A typical
  // Ctrl+V followed by Tab/click produced an out-of-order sequence:
  //   KEYDOWN-Ctrl, KEYUP-V, KEYUP-Ctrl, (…async…), KEYDOWN-V
  // FMX then routed the delayed KEYDOWN-V to the *newly* focused
  // control (e.g. the password field), which could confuse the state
  // machine and leave focus stranded on the wrong control.
  //
  // Queueing subsequent keys through the same callback backburner
  // guarantees strict sequential dispatch. The backburner drains
  // synchronously as soon as HandleClipboardReadText fires — and the
  // safety timeout ensures we're never stuck if the browser's
  // permission prompt is cancelled.
  if FClipboardTextPending then
  begin
    FClipboardReadCallbacks.push(
      procedure
      begin
        EnqueueEvent(evt);
      end
    );
    Exit(True);
  end;

  EnqueueEvent(evt);

  Result := True;
end;

// DoKeyUpEvent
//
function TWasmFresnelBrowserApi.DoKeyUpEvent(aEvent: TJSEvent): Boolean;
var
  evt : TWindowEvent;
  keyEvent : TJSKeyboardEvent absolute aEvent;
  //KeyKind : TKeyKind;
begin
  if FocusedCanvas = nil then Exit(False);

  evt := TWindowEvent.Create(FocusedCanvas.WindowCanvasID, WASMSG_KEYUP);
  //KeyKind:=KeyNameToKeyKind(KeyEvent.Key);
  //evt.param0:=KeyKind.KeyCode;
  //evt.Param1:=Ord(KeyKind.isSpecial);
  //evt.param2:=TWindowReference.EncodeShiftState(KeyEvent);
  SetWindowEventFromKeyboardEvent(evt, keyEvent);

  aEvent.preventDefault;
  aEvent.cancelBubble:=True;

  // Same backburner rule as DoKeyDownEvent so the KEYUP of the paste key
  // (e.g. 'V') cannot overtake the deferred KEYDOWN. Without this, FMX
  // briefly sees the 'V' "released" before it's "pressed", which —
  // combined with the Ctrl release firing immediately — left the edit's
  // caret/selection state inconsistent and prevented subsequent focus
  // changes from taking effect.
  if FClipboardTextPending then
  begin
    FClipboardReadCallbacks.push(
      procedure
      begin
        EnqueueEvent(evt);
      end
    );
    Exit(True);
  end;

  EnqueueEvent(Evt);

  Result := True;
end;

// DoGlobalEnter
//
procedure TWasmFresnelBrowserApi.DoGlobalEnter(aEvent : TJSEvent);
begin
  FocusedCanvas := FLastFocused;
end;

// DoGlobalLeave
//
procedure TWasmFresnelBrowserApi.DoGlobalLeave(aEvent : TJSEvent);
begin
  FLastFocused := FocusedCanvas;
  FocusedCanvas := nil;
end;

// DoGlobalClick
//
procedure TWasmFresnelBrowserApi.DoGlobalClick(aEvent : TJSEvent);
begin
  if aEvent.targetElement.closest('.fresnel-window') = nil then
    FocusedCanvas := nil;
end;

// DoBroadcast
//
procedure TWasmFresnelBrowserApi.DoBroadcast(aEvent: TJSEvent);
var
  evt : TWindowEvent;
begin
  evt := TWindowEvent.Create(0, WASMSG_BROADCAST);
  evt.param0 := LongInt(TJSObject(aEvent)['param0']);
  EnqueueEvent(evt);
  aEvent.preventDefault;
end;

// DoVisibilityChange
//
procedure TWasmFresnelBrowserApi.DoVisibilityChange(aEvent: TJSEvent);
var
  lEvt : TWindowEvent;
  lVisible : LongInt;
begin
  if Document['hidden'] then
    lVisible := 0
  else
    lVisible := 1;

  lEvt := TWindowEvent.Create(0, WASMSG_DESKTOP_VISIBILITY);
  lEvt.Param0 := lVisible;
  EnqueueEvent(lEvt);
end;

// ClipboardReadText
//
function TWasmFresnelBrowserApi.ClipboardReadText(aUTF16SizePtr, aDataUTF16: TWasmPointer): TCanvasError;
var
  view : TJSDataView;
  size : LongInt;
begin
  if UpdateClipboardReadText then
  begin
    view := MemoryDataView;
    size := view.getInt32(aUTF16SizePtr, Env.IsLittleEndian);
    view.setInt32(aUTF16SizePtr, Length(FClipboardText), Env.IsLittleEndian);

    if size < Length(FClipboardText) then
      Exit(EWASMEVENT_BUFFER_SIZE);

    SetUTF16ToMem(aDataUTF16, FClipboardText);
    Result := EWASMEVENT_SUCCESS;
  end else Result := EWASMEVENT_TRY_AGAIN;
end;

// ClipboardWriteText
//
function TWasmFresnelBrowserApi.ClipboardWriteText(aTextUTF16: TWasmPointer; aUTF16Size: LongInt): TCanvasError;
var
  text : String;
begin
  text := GetUTF16FromMem(aTextUTF16, aUTF16Size);
  try
    window.navigator.ClipBoard.writeText(text);
    Result := EWASMEVENT_SUCCESS;
  except
    Result := EWASMEVENT_ERROR;
  end;
end;

// UpdateDisplayMediaINIData
//
procedure TWasmFresnelBrowserApi.UpdateDisplayMediaINIData;
var
  lResultStr : String;
  i : Integer;
  lDeviceCount : Integer;
begin
  // Initialize result string and device counter
  lResultStr := '';
  lDeviceCount := 0;

  // Get all display media streams
  for i := 0 to FVideoReferences.size - 1 do
  begin
    FVideoReferences.forEach(
      procedure (aValue, aKey : JSValue)
      var
        lRef : TVideoReference;
        lMediaStream : TJSMediaStream;
        hasAudioTracks : Boolean;
      begin
        lRef := TVideoReference(aValue);

        // Only include display media streams that are actively capturing
        if lRef.IsDisplayMedia and lRef.IsCapturing then
        begin
          // Check if the stream has audio tracks
          hasAudioTracks := False;
          if lRef.FVideoElement.srcObject <> nil then
          begin
            lMediaStream := TJSMediaStream(lRef.FVideoElement.srcObject);
            hasAudioTracks := (lMediaStream.getAudioTracks.length > 0);
          end;

          // Generate INI formatted output for video device
          Inc(lDeviceCount);

          lResultStr += '[Device' + IntToStr(lDeviceCount) + ']' + #13#10
                     + 'kind=videoinput' + #13#10
                     + 'deviceId=' + IntToStr(lRef.ID) + #13#10
                     + 'groupId=displaymedia' + #13#10
                     + 'label=Display Media' + #13#10
                     + 'width=' + IntToStr(lRef.FVideoElement.videoWidth) + #13#10
                     + 'height=' + IntToStr(lRef.FVideoElement.videoHeight) + #13#10
                     + #13#10;

          // If we have audio tracks, also add an audio device entry with the _audio suffix
          if hasAudioTracks then
          begin
            Inc(lDeviceCount);
            lResultStr += '[Device' + IntToStr(lDeviceCount) + ']' + #13#10
                       + 'kind=audioinput' + #13#10
                       + 'deviceId=' + IntToStr(lRef.ID) + '_audio' + #13#10
                       + 'groupId=displaymedia' + #13#10
                       + 'label=Display Media Audio' + #13#10
                       + #13#10;
          end;
        end;
      end
    );
  end;

  // If no devices were found, provide an empty but valid INI structure
  if lDeviceCount = 0 then
    lResultStr := '[DisplayMedia]' + #13#10 + 'count=0' + #13#10 + #13#10;

  // Update the stored INI data
  FEnumeratedDisplayMedia := lResultStr;
end;

// DoEnumerateUserMedia
//
procedure TWasmFresnelBrowserApi.DoEnumerateUserMedia(aCallbackWhenReady : TProc);

  procedure StopAllTracks(aMediaStream : TJSMediaStream);
  begin
    aMediaStream.getTracks.forEach(
      procedure (aTrack: TJSMediaStreamTrack)
      begin
        aTrack.stop;
      end
    );
  end;

  procedure EnumerateDevices(aCallbackWhenReady : TProc);
  var
    lEnumerationPromise : TJSPromise;
  begin
    lEnumerationPromise := Window.navigator.mediaDevices.enumerateDevices;
    lEnumerationPromise._then(
      function (aDevices: JSValue) : JSValue
      var
        lDevice : TJSMediaDeviceInfo;
        lDeviceArray : TJSMediaDeviceInfoArray absolute aDevices;
        lIniOutput : String;
        I : Integer;
      begin
        // Generate INI formatted output
        for I := 0 to lDeviceArray.Length - 1 do
        begin
          lDevice := lDeviceArray.Devices[I];

          lIniOutput += '[Device' + IntToStr(I+1) + ']' + #13#10
                     + 'kind=' + lDevice.kind + #13#10
                     + 'deviceId=' + lDevice.deviceId + #13#10
                     + 'groupId=' + lDevice.groupId + #13#10;

          if TJSObject(lDevice)['label'] then
            lIniOutput += 'label=' + lDevice.label_ + #13#10;

          lIniOutput += #13#10;
        end;
        FEnumeratedUserMedia := lIniOutput;
        aCallbackWhenReady();
      end
    ).catch(
      function (aValue: JSValue) : JSValue
      begin
        FEnumeratedUserMedia := '[Error]' + #13#10 + 'message=Enumeration rejected' + #13#10;
        aCallbackWhenReady();
      end
    );
  end;

begin
  FEnumeratedUserMedia := '';
  try
    // Request permission first to access media devices
    Window.navigator.mediaDevices.getUserMedia(JS.New(['video', true, 'audio', true]))._then(
      function(aMediaStream: JSValue) : JSValue
      begin
        StopAllTracks(TJSMediaStream(aMediaStream));
        EnumerateDevices(aCallbackWhenReady);
      end
    ).catch(
      function (aValue: JSValue) : JSValue
      begin
        EnumerateDevices(
          procedure
          begin
            if Copy(FEnumeratedUserMedia, 1, 7) <> '[Error]' then
              FEnumeratedUserMedia += '[Error]' + #13#10 + 'message=GetUserMedia rejected';
            aCallbackWhenReady();
          end
        );
      end
    );
  except
    on E: Exception do
    begin
      FEnumeratedUserMedia := '[Error]' + #13#10 + 'message=' + E.Message + #13#10 + 'name=' + E.ClassName;
      aCallbackWhenReady();
    end;
  end;
end;

// AllocateVideoReference
//
function TWasmFresnelBrowserApi.AllocateVideoReference: TVideoReference;
var
  lID : TVideoElementID;
begin
  Inc(vNextVideoID);
  lID := vNextVideoID;

  Result := TVideoReference.Create(vNextVideoID, Self);

  FVideoReferences.&set(lID, Result);
end;

// DeAllocateVideoElement
//
function TWasmFresnelBrowserApi.DeAllocateVideoElement(const aID: TVideoElementID): TCanvasError;
var
  lRef : TVideoReference;
begin
  lRef := GetVideoReference(aID);
  if lRef <> nil then
  begin
    lRef.StopCapture;
    FVideoReferences.delete(aID);

    if lRef.IsDisplayMedia then
      UpdateDisplayMediaINIData;

    Result := ECANVAS_SUCCESS;
  end else Result := ECANVAS_NOVIDEO;
end;

// GetVideoReference
//
function TWasmFresnelBrowserApi.GetVideoReference(const aID: TVideoElementID): TVideoReference;
var
  lJSRef : JSValue;
begin
  lJSRef := FVideoReferences.get(aID);
  if lJSRef then
    Exit(TVideoReference(lJSRef))
  else Result := nil;
end;

// GetMediaStreamReference
//
function TWasmFresnelBrowserApi.GetMediaStreamReference(const aDeviceId: String): TJSMediaStream;
var
  lVideoElementId: TVideoElementID;
  lVideoRef: TVideoReference;
begin
  if Assigned(FOnGetMediaStreamReference) then
  begin
    Result := FOnGetMediaStreamReference(aDeviceId);
    if Result <> nil then
      Exit;
  end
  else
    Result := nil;

  lVideoElementId := StrToInt(LeftStr(aDeviceId, Pos('_', aDeviceId) - 1));

  lVideoRef := GetVideoReference(lVideoElementId);

  if (lVideoRef <> nil) and lVideoRef.IsCapturing then
    Result := TJSMediaStream(lVideoRef.FVideoElement.srcObject);
end;

// UpdateClipboardReadText
//
function TWasmFresnelBrowserApi.UpdateClipboardReadText : Boolean;
begin
  if FClipboardTextPending then
    Exit(False);

  if TJSDate.now <= FClipboardExpirationTimestamp then
    Exit(True);

  DoClipboardReadText(nil);
end;

// DoClipboardReadText
//
procedure TWasmFresnelBrowserApi.DoClipboardReadText(const aCallbackWhenReady : TProc);
begin
  if Assigned(aCallbackWhenReady) then
    FClipboardReadCallbacks.push(aCallbackWhenReady);

  if FClipboardTextPending then Exit;

  // HandleClipboardReadText asserts FClipboardTextPending=True at entry.
  // The previous code called it here with FClipboardTextPending still False,
  // which (a) triggered AssertionFailed in debug builds and (b) silently
  // ran HandleClipboardReadText in an unexpected state in release builds
  // — symptom: pending callbacks could end up fired before the flag was
  // cleared, leaving the Ctrl+V keydown dispatch racey with subsequent
  // mouse/focus events and stranding the password field focus.
  //
  // Latch the flag BEFORE HandleClipboardReadText so the invariant holds
  // on both the fast (no-focus) and the async-promise paths.
  FClipboardTextPending := True;
  FClipboardText := '';
  FClipboardExpirationTimestamp := TJSDate.now + cClipboardTextTTLinMilliseconds;
  Inc(FClipboardPendingToken);

  // Safety timeout: arm a setTimeout tied to the current pending token
  // so that if the readText Promise never settles (e.g. user dismisses
  // Chrome's clipboard permission prompt), the backburner of deferred
  // key events (DoKeyDownEvent/DoKeyUpEvent while FClipboardTextPending
  // is True) still gets drained. The token check protects against a
  // stale timer firing after a fresh read has already started.
  TimeoutCurrentClipboardRead(FClipboardPendingToken);

  if not document.hasFocus then
  begin
    HandleClipboardReadText('');
    Exit;
  end;

  try
    window.navigator.ClipBoard.readText._then(
      TJSPromiseResolver(@HandleClipboardReadText),
      function (aValue : JSValue) : JSValue
      begin
        HandleClipboardReadText('');
        Console.Debug('UpdateClipboardReadText rejected ', aValue);
      end
    ).catch(
      function (aValue : JSValue) : JSValue
      begin
        HandleClipboardReadText('');
        Console.Debug('UpdateClipboardReadText catch ', aValue);
      end
    );
  except
    on E: Exception do
    begin
      HandleClipboardReadText('');
      Console.Debug('UpdateClipboardReadText: ', E.Message);
    end;
  end;
end;

// TimeoutCurrentClipboardRead
//
// Schedule a safety timer that force-completes the current clipboard read
// if it hasn't settled within cClipboardSafetyTimeoutMs. Tied to the
// pending token so only the matching read is affected.
procedure TWasmFresnelBrowserApi.TimeoutCurrentClipboardRead(aToken : Integer);
begin
  window.setTimeout(
    procedure
    begin
      // Only force-complete if:
      //   (a) a read is still pending (someone may have already completed it)
      //   (b) the token still matches (no newer read has replaced us)
      if FClipboardTextPending and (FClipboardPendingToken = aToken) then
      begin
        Console.Debug('ClipboardReadText safety timeout after ',
                      cClipboardSafetyTimeoutMs, ' ms — draining callbacks');
        HandleClipboardReadText('');
      end;
    end,
    cClipboardSafetyTimeoutMs);
end;

// HandleClipboardReadText
//
procedure TWasmFresnelBrowserApi.HandleClipboardReadText(const aText: String);
var
  i : Integer;
  oldCallbacks : TJSArray;
begin
  Assert(FClipboardTextPending);
  FClipboardTextPending := False;
  FClipboardText := aText;
  if FClipboardReadCallbacks.Length > 0 then
  begin
    oldCallbacks := FClipboardReadCallbacks;
    FClipboardReadCallbacks := TJSArray.new;
    for i := 0 to oldCallbacks.Length-1 do
      TProc(oldCallbacks[i])();
  end;
end;

// UserMediaStartCapture
//
function TWasmFresnelBrowserApi.UserMediaStartCapture(
  aDeviceID_UTF16: TWasmPointer; aDeviceID_UTF16Size: Integer;
  aVideoID: TWasmPointer;
  aResolutionWidth, aResolutionHeight : Integer;
  aOptions : Integer
  ): TCanvasError;
var
  lVideoRef : TVideoReference;
  lDeviceID : String;

  procedure BlurCapture(aTimeStamp : TJSDOMHighResTimeStamp; aSnapShot : TJSImageBitmap);
  var
    lCallback : JSValue;
    lBitmapID : Integer;
  begin
    lCallback := InstanceExports['__fresnel_usermedia_frame'];
    if lCallback then
    begin
      if FSelfieSegmentation.IsReady then
      begin
        FSelfieSegmentation.Send(aSnapShot);
        FSelfieSegmentation.OnResults :=
          procedure (aResults : TJSSelfieSegmentationResults)
          begin
            lBitmapID := StoreImageBitmap(ProcessSegmentationResult(
              aResults,
              1, // maskThreshold,
              7, // blurAmount,
              0, // erosionDilationAmount,
              0, // edgeEnhancementAmount,
              5  // featheringAmount
            ));
            TUserMediaFrameCallback(lCallback)(aTimeStamp, lVideoRef.FID, lBitmapID);
          end
        ;
      end;
    end
    else aSnapShot.close;
  end;

  procedure DirectCapture(aTimeStamp : TJSDOMHighResTimeStamp; aSnapShot : TJSImageBitmap);
  var
    lCallback : JSValue;
    lBitmapID : Integer;
  begin
    lCallback := InstanceExports['__fresnel_usermedia_frame'];
    if lCallback then
    begin
      lBitmapID := StoreImageBitmap(aSnapShot);
      TUserMediaFrameCallback(lCallback)(aTimeStamp, lVideoRef.FID, lBitmapID);
    end
    else aSnapShot.close;
  end;

  procedure NotifyCaptureError;
  var
    lCallback : JSValue;
  begin
    lCallback := InstanceExports['__fresnel_usermedia_capture_error'];
    if lCallback then
      TUserMediaCaptureErrorCallback(lCallback)(lVideoRef.FID);
  end;

begin

  // LEGACY MAIN THREAD IMPLEMENTATION

  lVideoRef := AllocateVideoReference;
  try
    lDeviceID := GetUTF16FromMem(aDeviceID_UTF16, aDeviceID_UTF16Size);

    if (aOptions and cUserMediaCaptureOption_SelfieSegmentationBlur) <> 0 then
    begin
      if FSelfieSegmentation = nil then
      begin
        FSelfieSegmentation := TFresnelSelfieSegmentation.Create('/SelfieSegmentation');
        FSelfieSegmentation.LoadModel(0);
      end;
      lVideoRef.StartCapture(lDeviceID, aResolutionWidth, aResolutionHeight, @BlurCapture, @NotifyCaptureError);
    end
    else
    begin
      lVideoRef.StartCapture(lDeviceID, aResolutionWidth, aResolutionHeight, @DirectCapture, @NotifyCaptureError)
    end;
    MemoryDataView.setInt32(aVideoID, lVideoRef.FID, Env.IsLittleEndian);
    Result := ECANVAS_SUCCESS;
  except
    on E: Exception do
    begin
      Writeln('In UserMediaStartCapture, ', E.ClassName, ': ', E.Message);
      DeAllocateVideoElement(lVideoRef.FID);
      Result := ECANVAS_EXCEPTION;
    end;
  end;
end;

// MediaStopCapture
//
function TWasmFresnelBrowserApi.MediaStopCapture(aVideoID: TVideoElementID): TCanvasError;
begin
  Result := DeAllocateVideoElement(aVideoID);
end;

// MediaIsCapturing
//
function TWasmFresnelBrowserApi.MediaIsCapturing(aVideoID: TVideoElementID): TCanvasError;
var
  lRef : TVideoReference;
begin
  lRef := GetVideoReference(aVideoID);
  if lRef = nil then
    Exit(ECANVAS_NOVIDEO)
  else if lRef.IsCapturing then
    Exit(ECANVAS_SUCCESS)
  else Result := ECANVAS_EXCEPTION;
end;

// DisplayMediaStartCapture
//
function TWasmFresnelBrowserApi.DisplayMediaStartCapture(
  aVideoIDPtr : TWasmPointer;
  aWithAudio : Integer
  ): TCanvasError;
var
  lVideoRef : TVideoReference;
  lVideoConstraints : TJSMediaConstraints;

  procedure ProcessCapture(aNow : TJSDOMHighResTimeStamp; metaData : TJSVideoFrameMetaData);
  var
    lCallback : JSValue;
  begin
    lCallback := InstanceExports['__fresnel_displaymedia_frame'];
    if not lCallback then Exit;

    window.createImageBitmap(lVideoRef.FVideoElement)._then(
      function (aValue : JSValue) : JSValue
      var
        lImageBitmap : TJSImageBitmap absolute aValue;
        lBitmapID : Integer;
      begin
        lBitmapID := StoreImageBitmap(lImageBitmap);
        TUserMediaFrameCallback(lCallback)(aNow, lVideoRef.FID, lBitmapID);
        lVideoRef.FVideoElement.requestVideoFrameCallback(@ProcessCapture);
      end
    );
  end;

  procedure OnDisplayMediaStream(aMediaStream : TJSMediaStream);
  begin
    lVideoRef.FVideoElement.srcObject := aMediaStream;
    lVideoRef.FVideoElement.play;
    lVideoRef.FIsDisplayMedia := True;

    lVideoRef.FRequestVideoFrameID := lVideoRef.FVideoElement.requestVideoFrameCallback(@ProcessCapture);

    UpdateDisplayMediaINIData;
  end;

begin

  // LEGACY MAIN THREAD IMPLEMENTATION

  lVideoRef := AllocateVideoReference;

  MemoryDataView.setInt32(aVideoIDPtr, lVideoRef.FID, Env.IsLittleEndian);

  lVideoConstraints := TJSMediaConstraints.new;
  lVideoConstraints.video := True;
  lVideoConstraints.audio := (aWithAudio <> 0);

  window.navigator.mediaDevices.getDisplayMedia(lVideoConstraints)._then(
    function (aValue : JSValue) : JSValue
    begin
      OnDisplayMediaStream(TJSMediaStream(aValue));
      Result := nil;
    end
  ).catch(
    function (aValue : JSValue) : JSValue
    var
      lCallback : JSValue;
    begin
      Console.error('DisplayMediaStartCapture error: ', aValue);
      // The user cancelled or denied the share; tell the WASM side (success=0)
      // so the stream reports the error instead of staying silently blank.
      lCallback := InstanceExports['__fresnel_displaymedia_capture_started'];
      if lCallback then
        TCaptureDisplayMediaStartedCallback(lCallback)(lVideoRef.FID, 0, 0);
      DeAllocateVideoElement(lVideoRef.FID);
      Result := nil;
    end
  );

  Result := ECANVAS_SUCCESS;
end;

// DisplayMediaEnumerate
//
function TWasmFresnelBrowserApi.DisplayMediaEnumerate(
 aUTF16StrPtr: TWasmPointer; aUTF16SizePtr: TWasmPointer): TCanvasError;
var
  lSizeInChar : Integer;
begin
  lSizeInChar := Length(FEnumeratedDisplayMedia);

  // Always return size
  MemoryDataView.setInt32(aUTF16SizePtr, lSizeInChar, Env.IsLittleEndian);

  if lSizeInChar <= MemoryDataView.getInt32(aUTF16SizePtr, Env.IsLittleEndian) then begin

    SetUTF16ToMem(aUTF16StrPtr, FEnumeratedDisplayMedia);
    Result := ECANVAS_SUCCESS;

  end else begin
    Result := ECANVAS_BUFFERSIZE;
  end;
end;

end.
